import { Component, OnDestroy, OnInit, SecurityContext, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { Title, DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { PoiActionService } from '../../core/poi/poi.action';
import { Observable, of } from 'rxjs';
import { Poi } from '../../core/poi/poi.model';
import { select } from '@angular-redux/store';
import { ApiService } from '../../services/api.service';
import { BreadcrumbService } from 'angular-crumbs';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { ThemePalette } from '@angular/material/core';
import { Orientation } from '@ngmodule/material-carousel';
import { BreakpointObserver } from '@angular/cdk/layout';
import { environment } from 'src/environments/environment';
import { Attachments } from 'src/app/core/attachments/attachments.model';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import * as pdfmake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Lightbox } from 'ngx-lightbox';

@Component({
  selector: 'app-single-poi-page',
  templateUrl: './single-poi-page.component.html',
  styleUrls: ['./single-poi-page.component.scss']
})
export class SinglePoiPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('map', {static: false}) mapElement: ElementRef;

  geopos_url = {
    'Cimbri dell\'Altopiano dei Sette Comuni': 'cimbri',
    'Friulani in Veneto': 'friuli',
    'Italiani in Slovenia': 'italiani-in-slovenia',
    'Ladini del Comelico': 'ladini',
    'Sloveni in Italia': 'sloveni-in-italia',
    'Friulani in Friuli Venezia Giulia': 'friuli-fvg',
  };

  geopos_color = {
    'Cimbri dell\'Altopiano dei Sette Comuni': 'blue',
    'Friulani in Veneto': 'green',
    'Italiani in Slovenia': 'orange',
    'Ladini del Comelico': 'red',
    'Sloveni in Italia': 'lime',
    'Friulani in Friuli Venezia Giulia': 'blue_green',
  };

  @select(['poi', 'current'])
  poi_current$: Observable<Poi>;
  poi_current: Poi;

  subscription = [];
  current_lang = 'it';
  staticMapSrc = '';
  staticMapSrcBigScreen = '';
  staticMapSrcSmallScreen = '';

  public parentHeight = 'auto';
  public timings = '250ms ease-in';
  public autoplay = true;
  public interval = 5000;
  public loop = true;
  public hideArrows = false;
  public hideIndicators = false;
  public color: ThemePalette = 'primary';
  public maxWidth = 'auto';
  public maintainAspectRatio = false;
  public proportion = 25;
  public slideHeight = '400px';
  public slides = 1;
  public overlayColor = 'rgba(255, 255, 255, 0.6)';
  public hideOverlay = true;
  public useKeyboard = true;
  public useMouseWheel = false;
  public orientation: Orientation = 'ltr';
  public log: string[] = [];
  exist_images = false;
  loading = false;

  titleAudioPlaying = false;
  attachmentAudioPlaying: number = null;

  activeInfowindow: google.maps.InfoWindow;

  album: {src: string, caption: string, thumb: string}[] = [];

  googleLinkText: string = '';
  areaLinkText: string = '';

  pdfPrimisText: string = '';
  pdfGeoposText: string = '';
  pdfThemPathsText: string = '';

  videos: SafeResourceUrl[] = [];

  audio: HTMLAudioElement;

  constructor(private titleService: Title,
              private route: Router,
              private activateroute: ActivatedRoute,
              private apiService: ApiService,
              private poiAction: PoiActionService,
              private breadcrumbservice: BreadcrumbService,
              private translate: TranslateService,
              private breakpointObserver: BreakpointObserver,
              private sanitizer: DomSanitizer,
              private lightbox: Lightbox) {
    this.translate.setDefaultLang('it');
    if (this.translate.currentLang) {
      this.current_lang = this.translate.currentLang;
      this.translate.use(this.current_lang);
    } else {
      this.translate.use('it');
      this.current_lang = this.translate.currentLang;
    }
    this.translate.get('breadcrumb.single_poi').subscribe((res: string) => {
      this.titleService.setTitle(res + ' - PRIMIS');
    });
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.changeLangFunction();
    });
  }

  ngOnInit(): void {
    this.subscription.push(
      this.poi_current$.subscribe((poi) => {
        this.poi_current = poi;
        if (!!this.poi_current) {
          this.changeLangFunction();
          if (this.checkAttachments(this.poi_current.attachments, 'picture')) {
            const pictures = this.singleTypeAttachments(this.poi_current.attachments, 'picture');
            this.slides = pictures.length;
            this.album = pictures.map(({file_byte}) => ({
              src: file_byte,
              caption: null,
              thumb: null
            }));
            this.exist_images = true;
          } else {
            this.hideArrows = true;
          }
          if (this.checkYoutubeVideo(this.poi_current.attachments)) {
            this.videos = this.youtubeVideos(this.poi_current.attachments).map(({file_byte}) =>
              this.sanitizer.bypassSecurityTrustResourceUrl(file_byte)
            );
          }
        }
      }),
      this.breakpointObserver.observe('(max-width: 500px)').subscribe(result => {
        if (result.matches) {
          this.staticMapSrc = this.staticMapSrcSmallScreen;
          this.slideHeight = '300px';
          this.hideIndicators = true;
        } else {
          this.staticMapSrc = this.staticMapSrcBigScreen;
          this.slideHeight = '450px';
          this.hideIndicators = false;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.mapInitializer();
  }

  ngOnDestroy(): void {
    this.poiAction.setPoiCurrent(null);
    this.subscription.forEach(x => x.unsubscribe());
    if (!!this.audio && (this.titleAudioPlaying || !!this.attachmentAudioPlaying)) {
      this.resetAudioPlaying();
    }
  }

  private mapInitializer(): void {
    const {poi_name, coords, geopos} = this.poi_current;

    const centerLat = coords.lat;
    const centerLon = coords.lon;
    const mapProperties = {
      center: new google.maps.LatLng(centerLat, centerLon),
      zoom: 11,
      restriction: {
        latLngBounds: {
          north: 47,
          south: 45,
          west: 10.9,
          east: 14.4
        },
        strictBounds: false,
      },
      mapTypeId: google.maps.MapTypeId.ROADMAP,
    };
    const map = new google.maps.Map(this.mapElement.nativeElement, mapProperties);

    const googleMapsUrl = `http://maps.google.com/maps?z=11&q=loc:${centerLat}+${centerLon}`;
    const areaUrl = `${environment.mainUrl}/home/aree/${this.geopos_url[geopos[0].name.it]}`;

    const contentString =
      '<div id="content">' +
      '<div id="siteNotice"></div>' +
      '<h3 id="firstHeading" class="firstHeading">' + poi_name[this.current_lang] + '</h3>' +
      '<div id="bodyContent">' +
      '<p><a href="' + googleMapsUrl + '" target="blank">' + this.googleLinkText + '</a></p>' +
      '<p><a href="' + areaUrl + '">' + this.areaLinkText + '</a></p>' +
      '</div>' +
      '</div>';

    const infowindow = new google.maps.InfoWindow({
      content: contentString,
    });
    const color = this.geopos_color[geopos[0].name.it];
    const marker = new google.maps.Marker({
      position: { lat: centerLat, lng: centerLon },
      map,
      icon: `/assets/map/markerPrimis_${color}.png`,
      title: poi_name[this.current_lang],
    });
    marker.addListener('click', () => {
      if (!!this.activeInfowindow) {
        this.activeInfowindow.close();
      }
      infowindow.open(map, marker);
      this.activeInfowindow = infowindow;
    });
  }

  checkAttachments(att_list: Attachments[], att_type: string, source_needed: boolean = false) {
    if (source_needed) {
      return att_list.some(({file_type, source}) => file_type === att_type && !!source);
    }
    return att_list.some(({file_type}) => file_type === att_type);
  }

  checkYoutubeVideo(att_list: Attachments[]) {
    return att_list.some(({file_type, source}) => file_type === 'video' && !source);
  }

  checkIfAttachments(att_list: Attachments[]) {
    return att_list.some(({file_type}) => file_type !== 'picture');
  }

  singleTypeAttachments(att_list: Attachments[], att_type: string, source_needed: boolean = false) {
    if (source_needed) {
      return att_list.filter(({file_type, source}) => file_type === att_type && !!source);
    }
    return att_list.filter(({file_type}) => file_type === att_type);
  }

  youtubeVideos(att_list: Attachments[]) {
    return att_list.filter(({file_type, source}) => file_type === 'video' && !source);
  }

  changeLangFunction(): void {
    this.current_lang = this.translate.currentLang;
    this.titleService.setTitle(this.poi_current.poi_name[this.current_lang] + ' - PRIMIS');
    this.breadcrumbservice.changeBreadcrumb(this.activateroute.snapshot, this.poi_current.poi_name[this.current_lang]);
    this.translate.get('single_poi.google_maps_link').pipe(
      first(),
      switchMap((res: string) => {
        this.googleLinkText = res;
        return this.translate.get('single_poi.area_link').pipe(first());
      }),
      switchMap((res: string) => {
        this.areaLinkText = res;
        return this.translate.get('pdf_download.primis').pipe(first());
      }),
      switchMap((res: string) => {
        this.pdfPrimisText = res;
        return this.translate.get('pdf_download.geopos').pipe(first());
      }),
      switchMap((res: string) => {
        this.pdfGeoposText = res;
        return this.translate.get('pdf_download.themes').pipe(first());
      }),
      tap((res: string) => {
        this.pdfThemPathsText = res;
        if (!!this.mapElement) {
          this.mapInitializer();
        }
      })
    ).subscribe();
  }

  routerFunction(geopos_array) {
    let new_route = this.geopos_url[geopos_array[0].name.it];
    this.route.navigate(['/home/aree/' + new_route]);
  }

  openImage(albumIdx: number) {
    this.lightbox.open(this.album, albumIdx);
  }

  downloadAtt(att_dict: Attachments) {
    if (!!att_dict.file_byte) {
      if (att_dict.file_type === 'audio') {
        this.playAudio(att_dict.file_byte, att_dict.id);
      } else {
        this.getDownloadableAtt(att_dict.file_byte, att_dict.name);
      }
    } else {
      this.loading = true;
      this.apiService.getAttachment(att_dict.id).pipe(
        tap((fileByte: string) => {
          this.loading = false;
          if (att_dict.file_type === 'audio') {
            this.playAudio(fileByte, att_dict.id);
          } else {
            this.getDownloadableAtt(fileByte, att_dict.name);
          }
        }),
        catchError((err) => {
          this.loading = false;
          console.error('Non è stato possibile recuperare l\'URL dell\'allegato richiesto');
          return of(err);
        })
      ).subscribe();
    }
  }

  getDownloadableAtt(base64File: string, fileName: string) {
    const arr = base64File.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    let new_blob = new Blob([u8arr], {type: mime});
    this.downloadFromBlob(new_blob, fileName);
  }

  downloadFromBlob(blobFile: Blob, fileName: string) {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute(
      'href',
      this.sanitizer.sanitize(
        SecurityContext.URL,
        this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blobFile))
      )
    );
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  titleAudioPresent() {
    return this.poi_current.attachments.some(({title_audio}) => title_audio === true);
  }

  playAudio(audioFileByte: string = null, attachmentId: number = null): void {
    if (this.titleAudioPlaying && !!attachmentId && !!audioFileByte) {
      this.resetAudioPlaying();
      this.attachmentAudioPlaying = attachmentId;
      this.audio = new Audio(audioFileByte);
      this.audio.play();
    } else if (!!this.attachmentAudioPlaying && attachmentId == null) {
      this.resetAudioPlaying();
      this.titleAudioPlaying = true;
      const index = this.poi_current.attachments.findIndex(({title_audio}) => title_audio === true);
      this.audio = new Audio(this.poi_current.attachments[index].file_byte);
      this.audio.play();
    } else if (this.titleAudioPlaying || !!this.attachmentAudioPlaying) {
      this.resetAudioPlaying();
    } else {
      if (audioFileByte) {
        this.attachmentAudioPlaying = attachmentId;
        this.audio = new Audio(audioFileByte);
      } else {
        this.titleAudioPlaying = true;
        const index = this.poi_current.attachments.findIndex(({title_audio}) => title_audio === true);
        this.audio = new Audio(this.poi_current.attachments[index].file_byte);
      }
      this.audio.play();
    }
  }

  private resetAudioPlaying() {
    this.audio.pause();
    this.titleAudioPlaying = false;
    this.attachmentAudioPlaying = null;
  }

  async downloadPdf(): Promise<void> {
    const documentDefinition = {
      info: {
        title: this.poi_current.poi_name[this.current_lang],
        author: 'Primis Project',
        subject: this.poi_current.poi_name[this.current_lang],
        keywords: this.poi_current.themes.map(({name}) => name[this.current_lang]).join(', '),
        creator: 'Primis Project',
        producer: 'Primis Project',
      },
      content: [
        {
          text: 'Primis Project',
          style: ['header']
        },
        {
          text: this.pdfPrimisText,
          style: ['quote']
        },
        '\n',
        {
          text: this.poi_current.poi_name[this.current_lang],
          style: ['subheader']
        },
        {
          text: this.poi_current.address,
          style: ['small', 'quote']
        },
        this.poi_current.description[this.current_lang],
        '\n\n',
        {
          text: this.pdfGeoposText,
          style: ['subheader']
        },
        this.poi_current.geopos[0].name[this.current_lang],
        '\n',
        {
          text: this.pdfThemPathsText,
          style: ['subheader']
        },
        this.poi_current.themes.map(({name}) => name[this.current_lang]).join(', '),
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          color: '#0097a7'
        },
        subheader: {
          fontSize: 15,
          bold: true,
          color: '#0097a7'
        },
        quote: {
          italics: true
        },
        small: {
          fontSize: 10
        }
      }
    };
    const img = this.poi_current.attachments.find(({file_type}) => file_type === 'picture');
    if (img) {
      const imgSize: {width: number, height: number} = await this.getImgWidthHeight(img);
      const maxImgHeight = 258;
      const maxImgWidth = 450;
      const ratio = imgSize.width / imgSize.height;
      let width = maxImgWidth;
      let height = maxImgHeight;
      if (ratio <= 1.1) {
        width = maxImgHeight * ratio;
      } else {
        height = maxImgWidth / ratio;
      }
      documentDefinition.content.push(
        '\n\n',
        {
          image: img.file_byte,
          width,
          height,
        }
      );
    }

    const pdfDocGenerator = pdfmake.createPdf(documentDefinition, null, null, pdfFonts.pdfMake.vfs);
    pdfDocGenerator.getBlob((data) => {
      this.downloadFromBlob(data, this.poi_current.poi_name[this.current_lang]);
    });
  }

  private getImgWidthHeight(img: Attachments): Promise<{width: number, height: number}> {
    return new Promise ((resolved, _) => {
      const pic = new Image();
      pic.onload = () => {
        resolved({width: pic.width, height: pic.height});
      };
      pic.src = img.file_byte;
    });
  }

  getBase64FromUrl(urlImg: string) {
    const canvas = document.createElement('canvas');
    const img = new Image();
    img.src = urlImg;
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
    };
    return canvas.toDataURL('image/png');
  }

  sanitizeUrl(url: string): string {
    return this.sanitizer.sanitize(
      SecurityContext.RESOURCE_URL,
      this.sanitizer.bypassSecurityTrustResourceUrl(url)
    );
  }
}
